import { Component, ElementRef, Input, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { camelCase } from 'lodash';
// Service
import { LoggerService, BroadcastService, ObjectUtils } from '@cv-invision/core';
import { SchemasService } from '../../../../../services/schemas.service';
import { ViewsService } from '../../../../../services/views.service';
import { VisibilityContext } from '../../../../../services/visibility-context';
import { LayoutRenderService } from '../../../services/layout-render.service';

@Component({
	selector: 'cv-schema-render-form-field-special',
	templateUrl: './form-field-special.component.html',
})
export class FormFieldSpecialComponent implements OnInit, OnDestroy {
	@ViewChild('reviewField', { read: ElementRef, static: false }) reviewField: ElementRef;
	@Input() options: any;
	@Input() indexTab;

	formControl: FormControl | FormArray;
	field: any;
	value: any;
	historical = [];

	paths = {
		original: '',
		jsonPath: '',
		schema: '',
		path: ''
	};

	show = {
		value: false,
		label: true
	};

	optionList = [];

	visible = true;
	active = false;

	get form(): FormGroup {
		return this.viewsService.form;
	}

	get historicalValues(): any {
		return this.viewsService.historical;
	}

	get isEditing(): boolean {
		return this.layoutService.activateEditableUX;
	}

	private atComponentDestroy = new Subject();

	constructor(
		private viewsService: ViewsService,
		protected schemaService: SchemasService,
		private broadcastService: BroadcastService,
		private layoutService: LayoutRenderService
	) {
		//
	}

	async ngOnInit(): Promise<void> {
		await this.schemaService.setPaths(this.paths, this.options);
		const schemaPath = `${this.options.path}.${this.options.showEnumValues.key}`;
		this.paths.schema = schemaPath;

		await this.initMainField();
		this.prepareFields();
		this.setSubscriptions();
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/**
	 * Initialize the specialfield
	 */
	async initMainField(): Promise<void> {
		await this.setFormControl();
		await this.setField();
		this.setValue();

		// await this.setHistorical();
		await this.isVisible();
	}

	/**
	 * set the subscription to data changes
	 */
	setSubscriptions(): void {
		// this.viewsService.historicalChanged.pipe(takeUntil(this.atComponentDestroy)).subscribe(async data => {
		// 	await this.setHistorical();
		// });

		this.viewsService.selectedRuleChanged.pipe(takeUntil(this.atComponentDestroy)).subscribe(async rule => {
			await this.highlightField(rule);
		});

		// this.form.valueChanges.pipe(takeUntil(this.atComponentDestroy)).subscribe(async data => {
		// 	// await this.setValue();
		// 	// await this.isVisible();
		// });

		this.optionList.forEach(optionItem => {
			const optionKey = this.options.showEnumValues.key;

			optionItem.formControl.valueChanges
				.pipe(takeUntil(this.atComponentDestroy))
				.subscribe(optionValue => {
					const optionSelectedValue = optionItem.field.Value;

					if (optionValue) {
						const newControl = {};
						newControl[optionKey] = new FormControl(optionSelectedValue);
						// newControl[optionKey].markAsDirty();

						const newFormGroup = new FormGroup(newControl);
						newFormGroup.markAsDirty();
						(this.formControl as FormArray).push(newFormGroup);
					} else {
						const index = (this.formControl as FormArray).controls.findIndex((control: any) => control.controls[optionKey].value === optionSelectedValue);
						if (index > -1) {
							(this.formControl as FormArray).removeAt(index);
						}
					}
					(this.formControl as FormArray).markAsDirty();
					this.setValue(true);
				});
		});
	}

	/**
	 * Set the field different paths,
	 */
	async setField(): Promise<void> {
		const field = await this.schemaService.getMetadataByPath(this.paths.schema);
		this.field = ObjectUtils.recursiveDeepCopy(field);

		if ('show' in this.options) {
			Object.assign(this.show, this.options.show);
		}

		if ('label' in this.options) {
			const newLabels = {
				UILabel: this.options.label,
				DisplayText: this.options.label
			};

			Object.assign(this.field, newLabels);
		}
	}

	/**
	 * Set the value of the field
	 */
	setValue(updateOptionsListValue?: boolean): void {
		this.value = null;
		this.value = this.formControl.value;

		if (updateOptionsListValue) {
			this.optionList.forEach(optionItem => {
				const optionValue = this.value.find(itm => itm[this.options.showEnumValues.key] === optionItem.field.Value) || null;
				optionItem.value = optionValue;
			});
		}
	}

	/**
	 * initialize formcontrol from the given path
	 */
	async setFormControl(): Promise<any> {
		let formData: any = this.form;

		this.paths.path.split('.').forEach(path => {
			const pathPattern = /(\[[0-9]+\])/g;
			const matches = path.match(pathPattern);
			let isArray = false;
			let pathIndex;

			if (matches) {
				isArray = true;
				pathIndex = matches[0].replace(/([[\]])/g, '');
				path = path.replace(pathPattern, '');
			}

			formData = formData.get(path);
			if (isArray) {
				formData = formData.at(pathIndex);
			}
		});

		this.formControl = formData;
	}

	/**
	 * Set the historical array values
	 */
	async setHistorical(): Promise<void> {
		const pattern = `$..[?(@.path=="${this.paths.original}")]`;
		this.historical = (this.schemaService.query(this.historicalValues, pattern) || []).map(jpChanges => {
			return {
				Val: jpChanges.previousValue
			};
		});
	}

	/**
	 * Set the historical array values for non standarizes return values
	 */
	async setHistoricalNonStandarized(fieldValue: any): Promise<void> {
		if (fieldValue.Historical.length > 0) {
			const formated = [];
			fieldValue.Historical.forEach(item => {
				const groupParse: any = [];
				Object.keys(item).forEach(key => {
					const nKey = camelCase(key);
					groupParse[nKey] = item[key];
				});
				formated.push(Object.assign({}, groupParse));
			});
			this.historical = formated;
		}
	}

	/**
	 * open historical component
	 * @param item
	 * @param historical
	 */
	async showHistorical(elem: ElementRef): Promise<void> {
		const { height, left, width, top } = await elem.nativeElement.getBoundingClientRect();
		const offsetHeight = height;
		const offsetLeft = left - (width / 2);
		const offsetTop = top;
		const offsetWidth = width;
		const scrollTop = 0;

		this.broadcastService.broadcast({
			name: 'historicalValueEvent',
			event: {
				isOpen: true,
				data: this.historical,
				pos: { offsetHeight, offsetLeft, offsetTop, offsetWidth, scrollTop }
			}
		});
	}

	/**
	 * Evaluate the conditionality to show/hide the field
	 */
	async isVisible(): Promise<void> {
		let visible = true;
		if (this.field.Conditionally !== undefined && typeof this.field.Conditionally === 'function') {
			const evaluation = await this.field.Conditionally({}, new VisibilityContext(this.formControl, this.schemaService));
			visible = evaluation as boolean;

			if (!visible) {
				this.formControl.setValue('', { onlySelf: true, emitEvent: false });
			}
		}

		this.visible = visible;
	}

	/**
	 * Mark the field as active and scroll to the element
	 * @param rule
	 */
	async highlightField(rule: any): Promise<void> {
		this.active = rule.indexOf(this.paths.original) > -1;

		if (this.active) {
			let scrollOptions: any = false;
			if (window.innerHeight > 750) {
				scrollOptions = {
					behavior: 'smooth',
					block: 'start'
				};
			}
			this.reviewField.nativeElement.scrollIntoView(scrollOptions);
		}
	}

	/**
	 *
	 */
	prepareFields(): void {
		const fields = this.getInputs(this.field.Enumeration) || [];

		fields.forEach(field => {
			const fieldMetadata = {
				Name: this.options.showEnumValues.key,
				DisplayText: field.Value,
				DataType: 'bool-with-value',
				Value: field.Key
			};
			const optionValue = this.value.find(itm => itm[this.options.showEnumValues.key] === field.Key) || null;
			const fieldObj = {
				fieldOptions: {
					displayType: 'bool-with-value'
				},
				formControl: new FormControl(optionValue),
				field: fieldMetadata,
				value: optionValue,
				historical: [],
				show: {
					label: false,
					value: true
				}
			}

			this.optionList.push(fieldObj);
		});
	}

	/**
	 * Recursive function that will flat the enumsvalues
	 *
	 * @param enums
	 */
	getInputs(enums: any[]): any[] {
		let returnElems = [];

		enums.forEach(enumm => {
			if ('Children' in enumm) {
				const childrenEnums = this.getInputs(enumm.Children);
				if (childrenEnums.length) {
					returnElems = [...returnElems, ...childrenEnums];
				}
			} else {
				returnElems.push(enumm);
			}
		});

		return returnElems;
	}

}
